/** Семейства устройств, для которых у нас есть свои инструкции по уведомлениям. */
export type DevicePlatform = "windows" | "macos" | "linux" | "android" | "ios";

/** То, что нужно знать о браузере, чтобы определить устройство: так проще подставить в тесте. */
export interface PlatformSource {
  userAgent: string;
  maxTouchPoints?: number;
  userAgentData?: { platform?: string };
}

/**
 * Определяет устройство по данным браузера.
 *
 * Возвращает null, если узнать не удалось: интерфейс тогда не угадывает, а
 * предлагает выбрать самому. Порядок проверок важен: Android в user agent
 * содержит слово Linux, а iPad с iPadOS выдаёт себя за Mac и отличается от
 * него только сенсорным экраном.
 */
export function detectPlatform(source: PlatformSource): DevicePlatform | null {
  const hinted = source.userAgentData?.platform?.toLowerCase() ?? "";
  const agent = source.userAgent.toLowerCase();

  if (hinted === "android" || agent.includes("android")) return "android";
  if (/iphone|ipad|ipod/.test(agent)) return "ios";
  if (hinted === "windows" || agent.includes("windows")) return "windows";
  if (hinted === "macos" || agent.includes("macintosh") || agent.includes("mac os x")) {
    return (source.maxTouchPoints ?? 0) > 1 ? "ios" : "macos";
  }
  if (hinted === "linux" || hinted === "chrome os" || agent.includes("linux") || agent.includes("cros")) {
    return "linux";
  }

  return null;
}

/** Устройство, с которого открыта страница. Вне браузера (SSR, тесты без DOM) — null. */
export function currentPlatform(): DevicePlatform | null {
  if (typeof navigator === "undefined") return null;
  const { userAgent, maxTouchPoints, userAgentData } = navigator as Navigator & {
    userAgentData?: { platform?: string };
  };

  return detectPlatform({ userAgent, maxTouchPoints, userAgentData });
}

const platformNames: Record<DevicePlatform, string> = {
  windows: "Windows",
  macos: "macOS",
  linux: "Linux",
  android: "Android",
  ios: "iPhone или iPad",
};

/** Название браузера для списка устройств; порядок важен: Chrome есть в user agent у большинства. */
export function browserName(userAgent: string): string | null {
  if (/edg(?:e|a|ios)?\//i.test(userAgent)) return "Edge";
  if (/opr\/|opera/i.test(userAgent)) return "Opera";
  if (/yabrowser/i.test(userAgent)) return "Яндекс Браузер";
  if (/samsungbrowser/i.test(userAgent)) return "Samsung Internet";
  if (/firefox|fxios/i.test(userAgent)) return "Firefox";
  if (/chrome|chromium|crios/i.test(userAgent)) return "Chrome";
  if (/safari/i.test(userAgent)) return "Safari";

  return null;
}

/**
 * Имя устройства в списке подписок: «Chrome · Windows». Раньше туда попадала
 * сырая строка user agent, по которой человек не мог узнать свой телефон.
 * Установленное приложение отмечается отдельно — его подписка живёт
 * независимо от подписки браузера.
 */
export function describeDevice(source: PlatformSource, standalone: boolean): string {
  const parts = [
    browserName(source.userAgent),
    (() => {
      const platform = detectPlatform(source);
      return platform === null ? null : platformNames[platform];
    })(),
  ].filter((part): part is string => part !== null);
  const name = parts.length > 0 ? parts.join(" · ") : "Неизвестное устройство";

  return standalone ? `${name} (приложение)` : name;
}
